import { useState, useEffect, useCallback, useRef } from 'react';
import { useDebouncedValue } from './useDebouncedValue';

// Helper to get/set sort preferences from localStorage (matching useTablePreferences pattern)
const getSortPrefsKey = (tableId: string) => `table-prefs-${tableId}`;

const loadSortPreferences = (tableId: string): { column: string | null; direction: 'asc' | 'desc' } | null => {
  try {
    const stored = localStorage.getItem(getSortPrefsKey(tableId));
    if (!stored) return null;
    const prefs = JSON.parse(stored);
    if (prefs.sort) {
      return { column: prefs.sort.column, direction: prefs.sort.direction };
    }
    return null;
  } catch {
    return null;
  }
};

const saveSortPreferences = (tableId: string, column: string | null, direction: 'asc' | 'desc') => {
  try {
    const key = getSortPrefsKey(tableId);
    const stored = localStorage.getItem(key);
    const prefs = stored ? JSON.parse(stored) : { version: 1, columnKeys: [], columnWidths: {}, columnOrder: [], filters: {} };
    prefs.sort = column ? { column, direction } : null;
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Ignore localStorage errors
  }
};

// Helper to get/set filter preferences from localStorage
const loadFilterPreferences = (tableId: string): Record<string, string[]> => {
  try {
    const stored = localStorage.getItem(getSortPrefsKey(tableId));
    if (!stored) return {};
    const prefs = JSON.parse(stored);
    return prefs.filters || {};
  } catch {
    return {};
  }
};

const saveFilterPreferences = (tableId: string, filters: Record<string, string[]>) => {
  try {
    const key = getSortPrefsKey(tableId);
    const stored = localStorage.getItem(key);
    const prefs = stored ? JSON.parse(stored) : { version: 1, columnKeys: [], columnWidths: {}, columnOrder: [], sort: null };
    prefs.filters = filters;
    localStorage.setItem(key, JSON.stringify(prefs));
  } catch {
    // Ignore localStorage errors
  }
};

interface UseBaseCRUDProps<T> {
  service: {
    getAll: (params: any) => Promise<T[]>;
    search?: (query: string, column: string, params: any) => Promise<T[]>;
    getFilterOptions?: (column: string, filters?: Record<string, string[]>, extraParams?: any, signal?: AbortSignal) => Promise<any[]>;
  };
  tableId: string; // Required for persisting sort preferences
  pageSize?: number;
  defaultSearchColumn?: string;
  defaultSortColumn?: string;
  defaultSortDirection?: 'asc' | 'desc';
  initialSearchQuery?: string;
  initialSearchColumn?: string;
  initialLoadCount?: number; // Load this many records initially (for restoring scroll position)
  enableServerSideFilters?: boolean; // Enable server-side filtering
  extraFilterParams?: Record<string, any>; // Extra params to pass to filter options (e.g., is_archived, is_booking)
}

interface UseBaseCRUDReturn<T> {
  data: T[];
  loading: boolean;
  hasMore: boolean;
  searchQuery: string;
  searchColumn: string;
  sortColumn: string | null;
  sortDirection: 'asc' | 'desc';
  columnFilters: Record<string, string[]>;
  setSearchQuery: (query: string) => void;
  setSearchColumn: (column: string) => void;
  setSortColumn: (column: string | null) => void;
  setSortDirection: (direction: 'asc' | 'desc') => void;
  setColumnFilters: (filters: Record<string, string[]>) => void;
  handleSort: (column: string) => void;
  loadMore: () => void;
  refresh: () => void;
  clearAllFilters: () => void;
  getFilterOptions?: (column: string, currentFilters?: Record<string, string[]>) => Promise<any[]>;
}

export function useBaseCRUD<T>({
  service,
  tableId,
  pageSize = 100,
  defaultSearchColumn = '',
  defaultSortColumn,
  defaultSortDirection = 'asc',
  initialSearchQuery = '',
  initialSearchColumn,
  initialLoadCount,
  enableServerSideFilters = false,
  extraFilterParams
}: UseBaseCRUDProps<T>): UseBaseCRUDReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
  const [searchColumn, setSearchColumn] = useState(initialSearchColumn || defaultSearchColumn);

  // Initialize sort state from localStorage, falling back to defaults
  const [sortColumn, setSortColumn] = useState<string | null>(() => {
    const stored = loadSortPreferences(tableId);
    return stored?.column ?? defaultSortColumn ?? null;
  });
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
    const stored = loadSortPreferences(tableId);
    return stored?.direction ?? defaultSortDirection;
  });

  // Initialize filter state from localStorage
  const [columnFilters, setColumnFiltersState] = useState<Record<string, string[]>>(() => {
    if (!enableServerSideFilters) return {};
    return loadFilterPreferences(tableId);
  });

  // Wrapper to also persist to localStorage
  const setColumnFilters = useCallback((filters: Record<string, string[]>) => {
    setColumnFiltersState(filters);
    if (enableServerSideFilters) {
      saveFilterPreferences(tableId, filters);
    }
  }, [tableId, enableServerSideFilters]);

  const clearAllFilters = useCallback(() => {
    setColumnFilters({});
  }, [setColumnFilters]);

  const [currentPage, setCurrentPage] = useState(0);
  const isLoadingRef = useRef(false);
  const isInitialMount = useRef(true);
  const isFirstLoad = useRef(true);

  // Debounce search query
  const debouncedQuery = useDebouncedValue(searchQuery, 300);

  // Handle sort - toggles direction if same column, otherwise sets new column with asc
  const handleSort = useCallback((column: string) => {
    let newDirection: 'asc' | 'desc' = 'asc';
    if (sortColumn === column) {
      newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
      setSortDirection(newDirection);
    } else {
      setSortColumn(column);
      setSortDirection('asc');
      newDirection = 'asc';
    }
    // Persist to localStorage
    saveSortPreferences(tableId, column, newDirection);
  }, [sortColumn, sortDirection, tableId]);

  // Sync search column when default changes (only on mount or when defaultSearchColumn prop changes)
  // Don't reset searchColumn when user manually changes it
  useEffect(() => {
    if (defaultSearchColumn && !searchColumn) {
      // Only set if searchColumn is empty (initial state)
      setSearchColumn(defaultSearchColumn);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultSearchColumn]); // Only watch defaultSearchColumn, not searchColumn (intentionally omitted)

  // Load data
  const loadData = useCallback(async (reset = false) => {
    if (isLoadingRef.current) return;

    isLoadingRef.current = true;
    setLoading(true);
    try {
      // Use 0 for skip when resetting, otherwise calculate from current page
      const skip = reset ? 0 : currentPage * pageSize;

      // On first load with initialLoadCount, load that many records
      const effectiveLimit = (reset && isFirstLoad.current && initialLoadCount && initialLoadCount > pageSize)
        ? initialLoadCount
        : pageSize;

      let result: T[];

      // Build params with sorting and filters
      const params: any = {
        skip,
        limit: effectiveLimit,
        ...(sortColumn && { sort_column: sortColumn, sort_direction: sortDirection }),
        ...(enableServerSideFilters && Object.keys(columnFilters).length > 0 && { filters: columnFilters })
      };

      // Only search if we have a non-empty query and a search column
      if (debouncedQuery && debouncedQuery.trim() && searchColumn && service.search) {
        result = await service.search(debouncedQuery.trim(), searchColumn, params);
      } else {
        result = await service.getAll(params);
      }

      if (reset) {
        setData(result);
        // Calculate pages loaded based on effective limit
        const pagesLoaded = Math.ceil(result.length / pageSize);
        setCurrentPage(pagesLoaded);
        isFirstLoad.current = false;
      } else {
        setData(prev => [...prev, ...result]);
        setCurrentPage(prev => prev + 1);
      }

      setHasMore(result.length === effectiveLimit);
    } catch (error) {
      console.error('Error loading data:', error);
      // On error, ensure we reset loading state
      if (reset) {
        setData([]);
      }
    } finally {
      isLoadingRef.current = false;
      setLoading(false);
    }
  }, [currentPage, debouncedQuery, searchColumn, sortColumn, sortDirection, pageSize, service, initialLoadCount, enableServerSideFilters, columnFilters]);

  // Initial load and reload when service changes
  useEffect(() => {
    loadData(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service]);

  // React to debounced query, column, sort, and filter changes (skip initial mount)
  const prevParamsRef = useRef({
    query: debouncedQuery,
    column: searchColumn,
    sortCol: sortColumn,
    sortDir: sortDirection,
    filters: JSON.stringify(columnFilters)
  });

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      prevParamsRef.current = { query: debouncedQuery, column: searchColumn, sortCol: sortColumn, sortDir: sortDirection, filters: JSON.stringify(columnFilters) };
      return;
    }

    // Only trigger if params actually changed
    const prev = prevParamsRef.current;
    const currentFiltersStr = JSON.stringify(columnFilters);
    if (prev.query !== debouncedQuery || prev.column !== searchColumn || prev.sortCol !== sortColumn || prev.sortDir !== sortDirection || prev.filters !== currentFiltersStr) {
      prevParamsRef.current = { query: debouncedQuery, column: searchColumn, sortCol: sortColumn, sortDir: sortDirection, filters: currentFiltersStr };
      setCurrentPage(0);
      loadData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, searchColumn, sortColumn, sortDirection, columnFilters]);

  // Load more
  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      loadData(false);
    }
  }, [loading, hasMore, loadData]);

  // Refresh
  const refresh = useCallback(() => {
    setCurrentPage(0);
    loadData(true);
  }, [loadData]);

  // Get filter options callback (for server-side filters)
  // Uses passed currentFilters if provided, otherwise falls back to internal state
  const getFilterOptions = useCallback(async (column: string, currentFilters?: Record<string, string[]>) => {
    if (!service.getFilterOptions) return [];
    try {
      const filtersToUse = currentFilters ?? columnFilters;
      return await service.getFilterOptions(column, filtersToUse, extraFilterParams);
    } catch (error) {
      console.error('Error fetching filter options:', error);
      return [];
    }
  }, [service, columnFilters, extraFilterParams]);

  return {
    data,
    loading,
    hasMore,
    searchQuery,
    searchColumn,
    sortColumn,
    sortDirection,
    columnFilters,
    setSearchQuery,
    setSearchColumn,
    setSortColumn,
    setSortDirection,
    setColumnFilters,
    handleSort,
    loadMore,
    refresh,
    clearAllFilters,
    getFilterOptions: enableServerSideFilters && service.getFilterOptions ? getFilterOptions : undefined
  };
}
